import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

// ScreenplayDecorations — paints CSS classes onto screenplay markup so the
// editor renders Celtx-style layout without schema changes. Two formats:
//   • Paragraph prefix: `@@DLG `, `@@PAR `, `@@TRN ` — whole paragraph is the
//     cue; the literal tag is hidden via inline decoration.
//   • `char` mark — wraps a span of plain text. When the paragraph contains
//     only marked text (and optional whitespace), it renders as a full
//     character cue (Master Scenes indent). Inline mentions inside action
//     text get bold+uppercase styling without indent.
// Wire format stays plain markdown — the `char` mark serializes to
// `<char>NAME</char>` via tiptapMarkdown.js; raw text never carries the
// wrapper, so the caret can't get stranded inside an invisible close tag.

const PREFIX = {
  '@@SCN': 'scene',
  '@@DLG': 'dialog',
  '@@PAR': 'paren',
  '@@TRN': 'transition',
};

function classifyPrefix(text) {
  const trimmed = text.trimStart();
  const leading = text.length - trimmed.length;
  for (const [tag, cls] of Object.entries(PREFIX)) {
    if (trimmed.startsWith(tag + ' ') || trimmed === tag) {
      const name = trimmed.slice(tag.length).trim();
      const hideLen = trimmed.length === tag.length ? tag.length : tag.length + 1;
      return { cls, name, prefixStart: leading, prefixLen: hideLen };
    }
  }
  return null;
}

// True when every non-whitespace text node inside the paragraph carries the
// `char` mark. We use this to promote a paragraph that contains only a
// character cue (e.g. just "MARIA") into the Master Scenes indented cue
// layout. Whitespace-only text nodes don't disqualify it.
function paragraphIsCueOnly(paragraph) {
  let sawChar = false;
  let disqualified = false;
  paragraph.descendants((child) => {
    if (disqualified) return false;
    if (!child.isText) return undefined;
    const text = child.text || '';
    const hasMark = (child.marks || []).some((m) => m.type?.name === 'char');
    if (hasMark) {
      sawChar = true;
      return undefined;
    }
    if (text.trim() === '') return undefined;
    disqualified = true;
    return false;
  });
  return sawChar && !disqualified;
}

function firstCharName(paragraph) {
  let name = '';
  paragraph.descendants((child) => {
    if (name) return false;
    if (!child.isText) return undefined;
    const hasMark = (child.marks || []).some((m) => m.type?.name === 'char');
    if (hasMark) {
      name = (child.text || '').trim().toUpperCase();
      return false;
    }
    return undefined;
  });
  return name;
}

function buildDecorations(doc) {
  const decorations = [];

  doc.descendants((node, pos) => {
    if (node.type.name === 'paragraph') {
      if (paragraphIsCueOnly(node)) {
        decorations.push(
          Decoration.node(pos, pos + node.nodeSize, {
            class: 'screenplay-character',
            'data-character': firstCharName(node),
          }),
        );
      } else {
        const text = node.textContent || '';
        const hit = classifyPrefix(text);
        if (hit) {
          decorations.push(
            Decoration.node(pos, pos + node.nodeSize, {
              class: `screenplay-${hit.cls}`,
            }),
          );
          const tagFrom = pos + 1 + hit.prefixStart;
          const tagTo = tagFrom + hit.prefixLen;
          decorations.push(
            Decoration.inline(tagFrom, tagTo, { class: 'screenplay-cue-tag' }),
          );
        }
      }
    }

    if (node.isText) {
      const hasMark = (node.marks || []).some((m) => m.type?.name === 'char');
      if (hasMark) {
        const name = (node.text || '').trim().toUpperCase();
        decorations.push(
          Decoration.inline(pos, pos + node.nodeSize, {
            class: 'screenplay-char-inline',
            'data-character': name,
          }),
        );
      }
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const ScreenplayDecorations = Extension.create({
  name: 'screenplayDecorations',

  addProseMirrorPlugins() {
    const key = new PluginKey('screenplayDecorations');
    return [
      new Plugin({
        key,
        state: {
          init: (_, { doc }) => buildDecorations(doc),
          apply: (tr, old) => (tr.docChanged ? buildDecorations(tr.doc) : old),
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

export default ScreenplayDecorations;
